
import { User, Lead, Company, CallStat, UserRole, LeadComment, Appointment, AppointmentType, SubscriptionPlan, SystemConfig, PlanConfig, CurrencyConfig, LeadActivity, LeadActivityType, Notification, NotificationType, Opportunity, OpportunityItem, Task } from '../types';
import { MOCK_COMPANIES, MOCK_USERS, MOCK_LEADS, MOCK_CALL_STATS } from '../constants';
import { isDbMode, isDemoMode } from './runtimeMode';

const API_BASE = 'api/index.php';
const STORAGE_KEY_LEADS = 'orion_crm_leads_v2';
const STORAGE_KEY_COMPANIES = 'orion_crm_companies_v2';
const STORAGE_KEY_USERS = 'orion_crm_users_v2';
const STORAGE_KEY_CONFIG = 'orion_crm_config_v2';
const STORAGE_KEY_APPTS = 'orion_crm_appts_v2';
const STORAGE_KEY_ACTIVITIES = 'orion_crm_activities_v2';
const STORAGE_KEY_NOTIFICATIONS = 'orion_crm_notifications_v2';
const STORAGE_KEY_OPPORTUNITIES = 'orion_crm_opportunities_v1';
const STORAGE_KEY_TASKS = 'orion_crm_tasks_v1';
// Access token en sessionStorage (tab-isolé, effacé à la fermeture)
// Refresh token géré côté serveur via httpOnly cookie
const SESSION_KEY_ACCESS_TOKEN = 'orion_access_token';
export const AUTH_EXPIRED_EVENT = 'orion:auth-expired';
const ALLOW_LOCAL_AUTH_FALLBACK = isDemoMode && Boolean((import.meta as any).env?.DEV);

const DEFAULT_PLANS: PlanConfig[] = [
  { id: 'BASIC', label: 'Starter', priceMonthly: 29, priceYearly: 290, userLimit: 3, features: ['3 utilisateurs', 'Prospects illimités', 'Support email'] },
  { id: 'PRO', label: 'Growth', priceMonthly: 99, priceYearly: 990, userLimit: 15, features: ['15 utilisateurs', 'Prospects illimités', 'Support prioritaire', 'Analytics avancés'] },
  { id: 'ENTERPRISE', label: 'Scale', priceMonthly: 299, priceYearly: 2990, userLimit: 100, features: ['100 utilisateurs', 'Prospects illimités', 'Support 24/7', 'API complète', 'Personnalisation'] }
];

const DEFAULT_CURRENCIES: CurrencyConfig[] = [
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'GBP', name: 'Pound Sterling', symbol: '£' },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF' }
];

const DEFAULT_ACTIVITY_SECTORS: string[] = [
  'Informatique / SaaS',
  'Marketing & Communication',
  'BTP / Construction',
  'Sante',
  'Industrie',
  'Finance / Assurance',
  'Immobilier',
  'Commerce / Distribution',
  'Logistique / Transport',
  'Education / Formation'
];

// Amélioration de la gestion du localStorage avec alertes utilisateur
const loadFromStorage = <T>(key: string, fallback: T): T => {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return fallback;
    const parsed = JSON.parse(stored);
    if (Array.isArray(fallback) && !Array.isArray(parsed)) return fallback;
    return parsed;
  } catch (e) {
    console.error(`Erreur de chargement ${key}, utilisation des données par défaut.`, e);
    return fallback;
  }
};

let sessionLeads: Lead[] = isDemoMode ? loadFromStorage(STORAGE_KEY_LEADS, [...MOCK_LEADS]) : [];
let sessionCompanies: Company[] = isDemoMode ? loadFromStorage(STORAGE_KEY_COMPANIES, [...MOCK_COMPANIES]) : [];
let sessionUsers: (User & { password?: string })[] = isDemoMode ? loadFromStorage(STORAGE_KEY_USERS, [...MOCK_USERS]) : [];
let sessionConfig: SystemConfig = loadFromStorage(STORAGE_KEY_CONFIG, {
  plans: DEFAULT_PLANS,
  currencies: DEFAULT_CURRENCIES,
  activitySectors: DEFAULT_ACTIVITY_SECTORS
});
let sessionAppointments: Appointment[] = loadFromStorage(STORAGE_KEY_APPTS, []);
let sessionActivities: LeadActivity[] = loadFromStorage(STORAGE_KEY_ACTIVITIES, []);
let sessionNotifications: Notification[] = loadFromStorage(STORAGE_KEY_NOTIFICATIONS, []);
let sessionOpportunities: Opportunity[] = loadFromStorage(STORAGE_KEY_OPPORTUNITIES, []);
let sessionTasks: Task[] = loadFromStorage(STORAGE_KEY_TASKS, []);
let localComments: LeadComment[] = [];
let authExpiryNotified = false;

const notifyAuthExpired = () => {
  if (authExpiryNotified) return;
  authExpiryNotified = true;
  sessionStorage.removeItem(SESSION_KEY_ACCESS_TOKEN);
  localStorage.removeItem('auth_token'); // nettoyage rétrocompat
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
  }
  setTimeout(() => {
    authExpiryNotified = false;
  }, 1000);
};

// Compteur d'erreurs de stockage pour alerter l'utilisateur
let storageErrorCount = 0;

const saveToStorage = () => {
  try {
    if (isDemoMode) {
      localStorage.setItem(STORAGE_KEY_LEADS, JSON.stringify(sessionLeads));
      localStorage.setItem(STORAGE_KEY_COMPANIES, JSON.stringify(sessionCompanies));
      localStorage.setItem(STORAGE_KEY_USERS, JSON.stringify(sessionUsers));
    }
    localStorage.setItem(STORAGE_KEY_CONFIG, JSON.stringify(sessionConfig));
    localStorage.setItem(STORAGE_KEY_APPTS, JSON.stringify(sessionAppointments));
    localStorage.setItem(STORAGE_KEY_ACTIVITIES, JSON.stringify(sessionActivities));
    localStorage.setItem(STORAGE_KEY_NOTIFICATIONS, JSON.stringify(sessionNotifications));
    localStorage.setItem(STORAGE_KEY_OPPORTUNITIES, JSON.stringify(sessionOpportunities));
    localStorage.setItem(STORAGE_KEY_TASKS, JSON.stringify(sessionTasks));
    storageErrorCount = 0;
  } catch (e) {
    console.error("Erreur de sauvegarde localStorage", e);
    storageErrorCount++;
    
    if (storageErrorCount >= 3) {
      alert("⚠️ ATTENTION: Espace de stockage saturé!\n\nVos modifications récentes n'ont pas pu être sauvegardées.\nVeuillez libérer de l'espace ou exporter vos données.");
      storageErrorCount = 0;
    }
  }
};

// Fonction pour créer une activité dans la timeline
const createActivity = (
  leadId: string,
  userId: string,
  userName: string,
  type: LeadActivityType,
  description: string,
  metadata?: any
): LeadActivity => {
  const activity: LeadActivity = {
    id: 'activity_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5),
    leadId,
    userId,
    userName,
    type,
    description,
    metadata,
    createdAt: new Date().toISOString()
  };
  
  sessionActivities.push(activity);
  saveToStorage();

  if (isDbMode) {
    safeFetch(`${API_BASE}?action=add_lead_activity`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: activity.id,
        leadId: activity.leadId,
        userId: activity.userId,
        userName: activity.userName,
        type: activity.type,
        description: activity.description,
        metadata: activity.metadata || null
      })
    }).catch(() => {});
  }
  
  return activity;
};

// Timeout augmenté à 3 secondes
const refreshAuthToken = async (): Promise<boolean> => {
  try {
    // Le refresh token est dans le httpOnly cookie, envoyé automatiquement via credentials: 'include'
    const res = await fetch(`${API_BASE}?action=refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({})
    });
    if (!res.ok) return false;
    const data = await res.json();
    if (!data?.token) return false;
    sessionStorage.setItem(SESSION_KEY_ACCESS_TOKEN, data.token);
    return true;
  } catch {
    return false;
  }
};

const safeFetch = async (url: string, options?: RequestInit & { timeout?: number }, retried = false) => {
  try {
    if (typeof AbortController === 'undefined') return null;
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), options?.timeout || 10000);
    const token = sessionStorage.getItem(SESSION_KEY_ACCESS_TOKEN) || localStorage.getItem('auth_token');
    const headers: HeadersInit = {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options?.headers || {})
    };

    try {
      const res = await fetch(url, { ...options, headers, credentials: 'include', signal: controller.signal });
      clearTimeout(id);
      const rotatedToken = res.headers.get('X-Access-Token');
      if (rotatedToken) {
        sessionStorage.setItem(SESSION_KEY_ACCESS_TOKEN, rotatedToken);
      }
      const isAuthEndpoint = url.includes('action=login') || url.includes('action=refresh');
      if (res.status === 401) {
        if (!isAuthEndpoint && !retried) {
          const refreshed = await refreshAuthToken();
          if (refreshed) {
            return safeFetch(url, options, true);
          }
        }
        if (!isAuthEndpoint && token) {
          notifyAuthExpired();
        }
      }
      if (res.status === 409) {
        const text409 = await res.text();
        try { return JSON.parse(text409); } catch { return null; }
      }
      if (!res.ok) {
        console.warn(`API error: ${res.status} ${res.statusText}`);
        return null;
      }
      const text = await res.text();
      if (!text || text.trim().startsWith('<') || (!text.trim().startsWith('{') && !text.trim().startsWith('['))) {
        console.warn('API returned non-JSON response');
        return null;
      }
      return JSON.parse(text);
    } catch (fetchError: any) {
      if (fetchError.name === 'AbortError') {
        console.warn('API timeout after 3s');
      } else {
        console.warn('API fetch error:', fetchError.message);
      }
      return null;
    }
  } catch (e) {
    return null;
  }
};

const safeFetchRaw = async (url: string): Promise<Response> => {
  const token = sessionStorage.getItem(SESSION_KEY_ACCESS_TOKEN) || localStorage.getItem('auth_token');
  return fetch(url, {
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
};

const mapLeadFromApi = (l: any): Lead => {
  if (!l) throw new Error("Donnée invalide");
  return {
    id: String(l.id), 
    companyId: String(l.company_id || l.companyId || ''),
    clientCompanyName: String(l.client_company_name || l.clientCompanyName || ''),
    tag: String(l.tag || ''),
    sectorActivity: String(l.sector_activity || l.sectorActivity || ''),
    name: String(l.name || 'Prospect Sans Nom'),
    email: String(l.email || ''),
    phone: String(l.phone || ''),
    status: (l.status && ['new', 'contacted', 'qualified', 'lost', 'won'].includes(l.status)) ? l.status : 'new',
    assignedTo: String(l.assigned_to || l.assignedTo || ''),
    isFavorite: l.isFavorite === true || l.isFavorite === 'true' || l.is_favorite === 1 || l.is_favorite === '1',
    additionalInfo: String(l.additionalInfo || l.additional_info || ''),
    potentialRevenue: String(l.potentialRevenue || l.potential_revenue || ''),
    createdAt: String(l.created_at || l.createdAt || new Date().toISOString()),
    source: String(l.source || ''),
    erpCustomerId: l.erp_customer_id || l.erpCustomerId || undefined,
  };
};

const mapAppointmentFromApi = (a: any): Appointment => ({
  id: String(a.id),
  leadId: String(a.lead_id || a.leadId || ''),
  userId: String(a.user_id || a.userId || ''),
  title: String(a.title || ''),
  description: String(a.description || ''),
  start: String(a.start_time || a.start || ''),
  end: String(a.end_time || a.end || ''),
  type: (a.type || AppointmentType.PHONE) as AppointmentType,
  clientEmail: a.client_email || a.clientEmail || undefined
});

const mapCommentFromApi = (c: any): LeadComment => ({
  id: String(c.id),
  leadId: String(c.lead_id || c.leadId || ''),
  userId: String(c.user_id || c.userId || ''),
  userName: String(c.user_name || c.userName || ''),
  content: String(c.content || ''),
  createdAt: String(c.created_at || c.createdAt || new Date().toISOString())
});

const parseMaybeJson = (value: any) => {
  if (!value) return undefined;
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
};

const mapActivityFromApi = (a: any): LeadActivity => ({
  id: String(a.id),
  leadId: String(a.lead_id || a.leadId || ''),
  userId: String(a.user_id || a.userId || ''),
  userName: String(a.user_name || a.userName || ''),
  type: (a.type || LeadActivityType.INFO_UPDATED) as LeadActivityType,
  description: String(a.description || ''),
  metadata: parseMaybeJson(a.metadata),
  createdAt: String(a.created_at || a.createdAt || new Date().toISOString())
});

const mapOpportunityItemFromApi = (i: any, fallbackOpportunityId?: string): OpportunityItem => ({
  id: String(i.id || ''),
  opportunityId: String(i.opportunity_id || i.opportunityId || fallbackOpportunityId || ''),
  label: String(i.label || ''),
  quantity: Number(i.quantity ?? 0),
  unitPrice: Number(i.unit_price ?? i.unitPrice ?? 0),
  totalPrice: Number(i.total_price ?? i.totalPrice ?? 0)
});

const mapOpportunityFromApi = (o: any): Opportunity => ({
  id: String(o.id || ''),
  companyId: String(o.company_id || o.companyId || ''),
  leadId: o.lead_id || o.leadId ? String(o.lead_id || o.leadId) : undefined,
  clientCompanyName: String(o.client_company_name || o.clientCompanyName || ''),
  subject: String(o.subject || ''),
  comment: String(o.comment || ''),
  totalAmount: Number(o.total_amount ?? o.totalAmount ?? 0),
  createdBy: String(o.created_by || o.createdBy || ''),
  createdAt: String(o.created_at || o.createdAt || new Date().toISOString()),
  items: Array.isArray(o.items) ? o.items.map((i: any) => mapOpportunityItemFromApi(i, o.id)) : []
});

const mapTaskFromApi = (t: any): Task => ({
  id: String(t.id || ''),
  companyId: String(t.company_id || t.companyId || ''),
  leadId: t.lead_id || t.leadId ? String(t.lead_id || t.leadId) : undefined,
  clientCompanyName: t.client_company_name || t.clientCompanyName || undefined,
  title: String(t.title || ''),
  comment: String(t.comment || ''),
  dueAt: String(t.due_at || t.dueAt || new Date().toISOString()),
  status: (t.status === 'done' ? 'done' : 'todo'),
  createdBy: String(t.created_by || t.createdBy || ''),
  createdAt: String(t.created_at || t.createdAt || new Date().toISOString()),
  updatedAt: t.updated_at || t.updatedAt || undefined,
  completedAt: t.completed_at || t.completedAt || undefined
});

export const api = {
  // ===== AUTHENTIFICATION =====
  
  async restoreSession(): Promise<User | null> {
    const token = sessionStorage.getItem(SESSION_KEY_ACCESS_TOKEN) || localStorage.getItem('auth_token');
    if (!token) return null;
    try {
      const data = await safeFetch(`${API_BASE}?action=me`);
      if (data && data.id && !data.error) {
        return {
          id: data.id,
          companyId: data.company_id || 'system',
          name: data.name,
          email: data.email,
          role: data.role,
        };
      }
    } catch {
      // token invalide ou expiré — la session ne peut pas être restaurée
    }
    return null;
  },

  async login(email: string, password?: string): Promise<User | null> {
    const cleanEmail = email.trim().toLowerCase();
    
    try {
      const data = await safeFetch(`${API_BASE}?action=login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: cleanEmail, password })
      });
      
      if (data && !data.error && data.id) {
        if (data.token) {
          sessionStorage.setItem(SESSION_KEY_ACCESS_TOKEN, data.token);
          localStorage.removeItem('auth_token'); // nettoyage ancienne clé
        }
        return { 
          id: data.id, 
          companyId: data.company_id || 'system', 
          name: data.name, 
          email: data.email, 
          role: data.role 
        };
      }
    } catch (e) {
      console.log('API PHP non disponible');
    }

    if (!ALLOW_LOCAL_AUTH_FALLBACK) {
      return null;
    }

    const localUser = sessionUsers.find(u => u.email.toLowerCase() === cleanEmail);
    
    if (localUser) {
      const storedPass = localUser.password || '123';
      if (password === storedPass || (!password && storedPass === '123')) {
        console.log('✓ Connexion via stockage local réussie');
        return localUser;
      }
    }
    
    return null;
  },

  // ===== GESTION UTILISATEURS =====

  async createUser(userData: Omit<User, 'id'> & { password?: string }): Promise<User> {
    const newUser = { 
      ...userData, 
      id: 'u_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5),
      password: userData.password || '123456'
    };
    sessionUsers = [...sessionUsers, newUser];
    saveToStorage();

    safeFetch(`${API_BASE}?action=create_user`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData)
    }).then(result => {
      if (result) console.log('✓ Utilisateur synchronisé avec API');
    }).catch(() => {});

    return newUser;
  },

  async updateUser(userId: string, updates: Partial<User> & { password?: string }): Promise<User> {
    const index = sessionUsers.findIndex(u => String(u.id) === String(userId));
    if (index !== -1) {
      sessionUsers[index] = { 
        ...sessionUsers[index], 
        ...updates,
        ...(updates.password && { password: updates.password })
      };
      saveToStorage();
      
      safeFetch(`${API_BASE}?action=update_user`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: userId, ...updates })
      }).catch(() => {});
      
      return sessionUsers[index];
    }
    throw new Error(`Utilisateur ${userId} non trouvé`);
  },

  async deleteUser(userId: string): Promise<void> {
    sessionUsers = sessionUsers.filter(u => String(u.id) !== String(userId));
    saveToStorage();
    
    await safeFetch(`${API_BASE}?action=delete_user`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: userId })
    });
  },

  async getSuperAdmins(): Promise<User[]> {
    if (isDbMode) {
      try {
        const apiData = await safeFetch(`${API_BASE}?action=get_system_users`);
        if (Array.isArray(apiData)) {
          return apiData.map((u: any) => ({
            id: String(u.id),
            companyId: String(u.company_id || 'system'),
            name: u.name,
            email: u.email,
            role: u.role as UserRole,
          }));
        }
      } catch (e) {
        console.warn('get_system_users failed, fallback local');
      }
    }
    return sessionUsers.filter(u => u.role === UserRole.SUPER_ADMIN);
  },

  async getCompanyUsers(companyId: string): Promise<User[]> {
    if (isDbMode) {
      const apiData = await safeFetch(`${API_BASE}?action=get_company_users&company_id=${companyId}`);
      if (Array.isArray(apiData)) {
        const mappedUsers = apiData.map((u: any) => ({
          id: String(u.id),
          companyId: String(u.company_id || u.companyId || ''),
          name: String(u.name || ''),
          email: String(u.email || ''),
          role: u.role || UserRole.USER
        }));
        sessionUsers = [
          ...sessionUsers.filter(u => String(u.companyId) !== String(companyId)),
          ...mappedUsers
        ];
        return mappedUsers;
      }
      return [];
    }
    return sessionUsers.filter(u => String(u.companyId) === String(companyId));
  },

  getLocalCompanyAdmin(companyId: string): User | undefined {
    return sessionUsers.find(u => 
      String(u.companyId) === String(companyId) && 
      u.role === UserRole.COMPANY_ADMIN
    );
  },

  // ===== CONFIGURATION SYSTÈME =====

  async getSystemConfig(): Promise<SystemConfig> {
    if (!sessionConfig.activitySectors || !Array.isArray(sessionConfig.activitySectors)) {
      sessionConfig = { ...sessionConfig, activitySectors: [...DEFAULT_ACTIVITY_SECTORS] };
      saveToStorage();
    }
    return sessionConfig;
  },

  async updateSystemConfig(newConfig: Partial<SystemConfig>): Promise<SystemConfig> {
    sessionConfig = { ...sessionConfig, ...newConfig };
    saveToStorage();
    return sessionConfig;
  },

  async getActivitySectors(): Promise<string[]> {
    if (isDbMode) {
      const apiData = await safeFetch(`${API_BASE}?action=get_activity_sectors`);
      if (Array.isArray(apiData)) {
        const sectors = apiData
          .map((row: any) => String(row.name || row.sector_name || ''))
          .filter((name: string) => name.trim() !== '');
        if (sectors.length > 0) {
          sessionConfig = { ...sessionConfig, activitySectors: sectors };
          saveToStorage();
          return sectors;
        }
      }
    }

    if (!sessionConfig.activitySectors || sessionConfig.activitySectors.length === 0) {
      sessionConfig = { ...sessionConfig, activitySectors: [...DEFAULT_ACTIVITY_SECTORS] };
      saveToStorage();
    }

    return [...(sessionConfig.activitySectors || [])];
  },

  async addActivitySector(name: string): Promise<string[]> {
    const cleaned = String(name || '').trim();
    if (!cleaned) {
      throw new Error('Nom de secteur invalide');
    }

    if (isDbMode) {
      const apiResult = await safeFetch(`${API_BASE}?action=create_activity_sector`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: cleaned })
      });
      if (!apiResult || apiResult.success !== true) {
        throw new Error('Creation du secteur echouee cote base');
      }
      return await api.getActivitySectors();
    }

    const current = new Set((sessionConfig.activitySectors || []).map(s => s.trim()).filter(Boolean));
    current.add(cleaned);
    const next = Array.from(current).sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }));
    sessionConfig = { ...sessionConfig, activitySectors: next };
    saveToStorage();
    return next;
  },

  // ===== GESTION ENTREPRISES =====
  
  async createCompany(companyData: Partial<Company> & { adminName: string, adminEmail: string, adminPassword?: string }): Promise<Company> {
    const generatedId = companyData.id || ('c_' + Date.now().toString(36));
    const newComp: Company = {
      id: generatedId,
      name: companyData.name || 'Nouvelle Entreprise',
      plan: companyData.plan || SubscriptionPlan.BASIC,
      userLimit: companyData.userLimit || 3,
      activeLeads: 0,
      status: 'active',
      createdAt: new Date().toISOString(),
      currency: companyData.currency || 'EUR'
    };
    
    const newAdmin = {
      id: 'u_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5),
      companyId: generatedId,
      name: companyData.adminName,
      email: companyData.adminEmail,
      role: UserRole.COMPANY_ADMIN,
      password: companyData.adminPassword || '123456'
    };

    sessionCompanies = [newComp, ...sessionCompanies];
    sessionUsers = [...sessionUsers, newAdmin];
    saveToStorage();

    safeFetch(`${API_BASE}?action=create_company_full`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...companyData, id: generatedId })
    }).then(result => {
      if (result) console.log('✓ Entreprise synchronisée avec API');
    }).catch(() => {});

    return newComp;
  },

  async getCompanies(): Promise<Company[]> {
    if (isDbMode) {
      const apiData = await safeFetch(`${API_BASE}?action=get_companies`);
      if (Array.isArray(apiData)) {
        const mappedCompanies: Company[] = apiData.map((c: any) => {
          let revenueBrackets: string[] | undefined;
          let revenueColors: Record<string, string> | undefined;
          try { revenueBrackets = c.revenue_brackets ? JSON.parse(c.revenue_brackets) : undefined; } catch { revenueBrackets = undefined; }
          try { revenueColors = c.revenue_colors ? JSON.parse(c.revenue_colors) : undefined; } catch { revenueColors = undefined; }
          return {
            id: String(c.id),
            name: String(c.name || ''),
            plan: c.plan || SubscriptionPlan.BASIC,
            userLimit: Number(c.user_limit ?? c.userLimit ?? 3),
            activeLeads: Number(c.active_leads ?? c.activeLeads ?? 0),
            status: c.status || 'active',
            createdAt: String(c.created_at || c.createdAt || new Date().toISOString()),
            currency: String(c.currency || 'EUR'),
            address: c.address || undefined,
            phone: c.phone || undefined,
            email: c.email || undefined,
            website: c.website || undefined,
            revenueBrackets: Array.isArray(revenueBrackets) && revenueBrackets.length > 0 ? revenueBrackets : undefined,
            revenueColors: revenueColors && Object.keys(revenueColors).length > 0 ? revenueColors : undefined
          };
        });
        sessionCompanies = mappedCompanies;
        return mappedCompanies;
      }
      return [];
    }
    return sessionCompanies;
  },

  async updateCompany(companyId: string, updates: Partial<Company>): Promise<Company> {
    const index = sessionCompanies.findIndex(c => String(c.id) === String(companyId));
    if (index !== -1) {
      sessionCompanies[index] = { ...sessionCompanies[index], ...updates };
      saveToStorage();
      
      safeFetch(`${API_BASE}?action=update_company`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: companyId, ...updates })
      }).catch(() => {});
      
      return sessionCompanies[index];
    }
    throw new Error(`Entreprise ${companyId} non trouvée`);
  },

  async updateCompanyStatus(id: string, status: 'active' | 'suspended'): Promise<void> {
    sessionCompanies = sessionCompanies.map(c => c.id === id ? { ...c, status } : c);
    saveToStorage();
    await safeFetch(`${API_BASE}?action=update_company_status`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, status })
    });
  },

  // ===== GESTION PROSPECTS =====

  async getLeads(params: { companyId: string, userId: string, role: string, agentId?: string, dateFrom?: string, dateTo?: string }): Promise<Lead[]> {
    const apiData = await safeFetch(`${API_BASE}?action=get_leads&company_id=${params.companyId}`);
    if (Array.isArray(apiData)) {
      const mappedLeads = apiData.map(mapLeadFromApi);
      sessionLeads = [
        ...mappedLeads.filter(l => String(l.companyId) === String(params.companyId)),
        ...sessionLeads.filter(l => String(l.companyId) !== String(params.companyId))
      ];
    } else if (isDbMode) {
      throw new Error('Impossible de charger les prospects depuis la base');
    }
    
    // 2. FILTRER (depuis localStorage maintenant synchronisé)
    let filtered = sessionLeads.filter(l => String(l.companyId) === String(params.companyId));
    if (params.role === UserRole.USER) {
      filtered = filtered.filter(l => String(l.assignedTo) === String(params.userId));
    } else if (params.agentId && params.agentId !== 'all') {
      filtered = filtered.filter(l => String(l.assignedTo) === String(params.agentId));
    }
    
    if (params.dateFrom) {
      filtered = filtered.filter(l => new Date(l.createdAt) >= new Date(params.dateFrom!));
    }
    if (params.dateTo) {
      filtered = filtered.filter(l => new Date(l.createdAt) <= new Date(params.dateTo!));
    }
    
    return filtered.map(mapLeadFromApi);
  },

  async createLead(leadData: Partial<Lead> & { name: string, email: string, companyId: string }, currentUser?: { id: string, name: string }): Promise<Lead> {
    const generatedId = leadData.id || ('lead_' + Date.now().toString(36));
    const fullLead: Lead = {
      id: generatedId,
      companyId: String(leadData.companyId || ''),
      clientCompanyName: String(leadData.clientCompanyName || ''),
      tag: String(leadData.tag || ''),
      sectorActivity: String(leadData.sectorActivity || ''),
      name: String(leadData.name || 'Nouveau Prospect'),
      email: String(leadData.email || ''),
      phone: String(leadData.phone || ''),
      status: leadData.status || 'new',
      assignedTo: String(leadData.assignedTo || ''),
      createdAt: leadData.createdAt || new Date().toISOString(),
      potentialRevenue: leadData.potentialRevenue || '',
      additionalInfo: leadData.additionalInfo || '',
      isFavorite: leadData.isFavorite || false,
      source: leadData.source || ''
    };

    const apiResult = await safeFetch(`${API_BASE}?action=create_lead`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(fullLead)
    });
    
    if (apiResult?.warning === 'duplicate') {
      const dup = apiResult.duplicate;
      const dupInfo = `${dup.name} (${dup.email || dup.phone})`;
      throw new Error(`DUPLICATE:${dupInfo}`);
    }
    if (!apiResult || (!apiResult.success && !apiResult.id)) {
      if (isDbMode) {
        throw new Error('Création du prospect échouée côté base de données');
      }
      console.warn('⚠️  API a pas confirmé la création, stockage local seul');
    } else {
      console.log(`✓ Prospect "${fullLead.name}" créé en BD (API)`);
    }

    sessionLeads = [fullLead, ...sessionLeads];
    
    // 3. ACTIVITÉ : Création du prospect
    if (currentUser) {
      createActivity(
        generatedId,
        currentUser.id,
        currentUser.name,
        LeadActivityType.CREATED,
        `Prospect créé par ${currentUser.name}`
      );
    }

    // 4. NOTIFICATION : Prospect assigné
    if (fullLead.assignedTo && fullLead.assignedTo !== currentUser?.id) {
      await api.createNotification({
        userId: fullLead.assignedTo,
        type: NotificationType.LEAD_ASSIGNED,
        title: 'Nouveau prospect assigné',
        message: `${currentUser?.name || 'Un administrateur'} vous a assigné un nouveau prospect`,
        leadId: fullLead.id,
        leadName: fullLead.name,
        isRead: false
      });
    }
    
    saveToStorage();
    return fullLead;
  },

  async updateLead(leadId: string, updates: Partial<Lead>, currentUser?: { id: string, name: string }): Promise<Lead> {
    const index = sessionLeads.findIndex(l => String(l.id) === String(leadId));
    if (index !== -1) {
      const oldLead = sessionLeads[index];
      sessionLeads[index] = { ...sessionLeads[index], ...updates };
      
      // ACTIVITÉS
      if (currentUser) {
        // Changement de statut
        if (updates.status && updates.status !== oldLead.status) {
          const statusLabels: Record<string, string> = {
            'new': 'Nouveau',
            'contacted': 'Contacté',
            'qualified': 'Qualifié',
            'lost': 'Perdu',
            'won': 'Gagné'
          };
          createActivity(
            leadId,
            currentUser.id,
            currentUser.name,
            LeadActivityType.STATUS_CHANGED,
            `Statut changé de "${statusLabels[oldLead.status]}" à "${statusLabels[updates.status]}"`,
            {
              oldValue: statusLabels[oldLead.status],
              newValue: statusLabels[updates.status]
            }
          );
        }
        
        // Changement d'assignation
        if (updates.assignedTo && updates.assignedTo !== oldLead.assignedTo) {
          const oldUser = sessionUsers.find(u => u.id === oldLead.assignedTo);
          const newUser = sessionUsers.find(u => u.id === updates.assignedTo);
          createActivity(
            leadId,
            currentUser.id,
            currentUser.name,
            LeadActivityType.ASSIGNED,
            `Prospect réassigné à ${newUser?.name || 'Inconnu'}`,
            {
              oldValue: oldUser?.name || 'Non assigné',
              newValue: newUser?.name || 'Inconnu'
            }
          );

          // NOTIFICATION : Réassignation
          if (updates.assignedTo !== currentUser.id) {
            await api.createNotification({
              userId: updates.assignedTo,
              type: NotificationType.LEAD_ASSIGNED,
              title: 'Prospect réassigné',
              message: `${currentUser.name} vous a assigné le prospect ${oldLead.name}`,
              leadId: leadId,
              leadName: oldLead.name,
              isRead: false
            });
          }
        }
        
        // Toggle favori
        if (updates.isFavorite !== undefined && updates.isFavorite !== oldLead.isFavorite) {
          createActivity(
            leadId,
            currentUser.id,
            currentUser.name,
            LeadActivityType.FAVORITE_TOGGLED,
            updates.isFavorite ? 'Ajouté aux favoris' : 'Retiré des favoris'
          );
        }
        
        // Autres modifications
        if (updates.name || updates.email || updates.phone || updates.additionalInfo || updates.potentialRevenue || updates.clientCompanyName || updates.tag || updates.sectorActivity) {
          const changedFields: string[] = [];
          if (updates.name && updates.name !== oldLead.name) changedFields.push(`Nom: "${oldLead.name}" → "${updates.name}"`);
          if (updates.clientCompanyName !== undefined && updates.clientCompanyName !== (oldLead.clientCompanyName || '')) changedFields.push(`Entreprise: "${oldLead.clientCompanyName || ''}" → "${updates.clientCompanyName}"`);
          if (updates.email && updates.email !== oldLead.email) changedFields.push(`Email: "${oldLead.email}" → "${updates.email}"`);
          if (updates.phone !== undefined && updates.phone !== (oldLead.phone || '')) changedFields.push(`Tél: "${oldLead.phone || ''}" → "${updates.phone}"`);
          createActivity(
            leadId,
            currentUser.id,
            currentUser.name,
            LeadActivityType.INFO_UPDATED,
            changedFields.length > 0 ? changedFields.join(' | ') : 'Informations du prospect mises à jour'
          );
        }
      }
      
      saveToStorage();

      // SYNC API — attendre la confirmation
      try {
        const result = await safeFetch(`${API_BASE}?action=update_lead`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: leadId, ...updates })
        });
        if (result && result.success) {
          console.log(`✓ Prospect "${sessionLeads[index].name}" mis à jour en BD (API)`);
        } else {
          console.warn('⚠️ Sync API updateLead: réponse inattendue', result);
        }
      } catch (err: any) {
        console.warn('⚠️ Erreur sync API pour updateLead:', err.message);
      }
      
      return sessionLeads[index];
    }
    throw new Error(`Lead ${leadId} non trouvé`);
  },

  async deleteLead(leadId: string): Promise<void> {
    if (isDbMode) {
      try {
        const result = await safeFetch(`${API_BASE}?action=delete_lead`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: leadId })
        });
        if (!result || result.error) {
          console.warn('⚠️ Suppression API échouée:', result?.error || 'pas de réponse');
        }
      } catch (e) {
        console.warn('⚠️ Erreur réseau suppression:', e);
      }
    }
    sessionLeads = sessionLeads.filter(l => l.id !== leadId);
    sessionActivities = sessionActivities.filter(a => a.leadId !== leadId);
    sessionAppointments = sessionAppointments.filter(a => a.leadId !== leadId);
    saveToStorage();
  },

  // ===== ACTIONS EN MASSE =====

  async bulkUpdateLeads(ids: string[], field: 'status' | 'assigned_to', value: string): Promise<{ updated: number }> {
    if (isDbMode) {
      const result = await safeFetch(`${API_BASE}?action=bulk_update_leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids, field, value })
      });
      if (!result || result.error) {
        throw new Error(result?.error || 'Mise à jour en masse échouée');
      }
      return result;
    }
    // local fallback
    ids.forEach(id => {
      const lead = sessionLeads.find(l => l.id === id);
      if (lead) { (lead as any)[field === 'assigned_to' ? 'assignedTo' : field] = value; }
    });
    saveToStorage();
    return { updated: ids.length };
  },

  async bulkDeleteLeads(ids: string[]): Promise<{ deleted: number }> {
    if (isDbMode) {
      const result = await safeFetch(`${API_BASE}?action=bulk_delete_leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids })
      });
      if (!result || result.error) {
        throw new Error(result?.error || 'Suppression en masse échouée');
      }
      return result;
    }
    sessionLeads = sessionLeads.filter(l => !ids.includes(l.id));
    saveToStorage();
    return { deleted: ids.length };
  },

  // ===== TACHES =====

  async getTasks(filters: { companyId: string; leadId?: string; status?: 'todo' | 'done' }): Promise<Task[]> {
    if (isDbMode) {
      const query: string[] = [`company_id=${encodeURIComponent(filters.companyId)}`];
      if (filters.leadId) query.push(`lead_id=${encodeURIComponent(filters.leadId)}`);
      if (filters.status) query.push(`status=${encodeURIComponent(filters.status)}`);
      const apiData = await safeFetch(`${API_BASE}?action=get_tasks&${query.join('&')}`);
      if (Array.isArray(apiData)) {
        const mapped = apiData.map(mapTaskFromApi);
        sessionTasks = [
          ...sessionTasks.filter(t => String(t.companyId) !== String(filters.companyId)),
          ...mapped
        ];
        saveToStorage();
        return mapped;
      }
      throw new Error('Impossible de recuperer les taches depuis la base');
    }

    return sessionTasks.filter(t => {
      if (String(t.companyId) !== String(filters.companyId)) return false;
      if (filters.leadId && String(t.leadId || '') !== String(filters.leadId)) return false;
      if (filters.status && t.status !== filters.status) return false;
      return true;
    });
  },

  async createTask(payload: {
    companyId: string;
    leadId?: string;
    clientCompanyName?: string;
    title: string;
    comment?: string;
    dueAt: string;
  }, currentUser: { id: string; name: string }): Promise<Task> {
    const id = 'task_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5);
    const task: Task = {
      id,
      companyId: payload.companyId,
      leadId: payload.leadId,
      clientCompanyName: payload.clientCompanyName || undefined,
      title: payload.title,
      comment: payload.comment || '',
      dueAt: payload.dueAt,
      status: 'todo',
      createdBy: currentUser.id,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    if (isDbMode) {
      const apiResult = await safeFetch(`${API_BASE}?action=create_task`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(task)
      });
      if (!apiResult || !apiResult.id) {
        throw new Error('Creation de la tache echouee cote base');
      }
      const persisted = mapTaskFromApi(apiResult);
      sessionTasks = [persisted, ...sessionTasks];
      saveToStorage();
      return persisted;
    }

    sessionTasks = [task, ...sessionTasks];
    saveToStorage();
    return task;
  },

  async updateTask(taskId: string, updates: Partial<Task>): Promise<Task> {
    if (isDbMode) {
      const apiResult = await safeFetch(`${API_BASE}?action=update_task`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: taskId, ...updates })
      });
      if (!apiResult || !apiResult.success || !apiResult.task) {
        throw new Error('Mise a jour de la tache echouee cote base');
      }
      const persisted = mapTaskFromApi(apiResult.task);
      sessionTasks = sessionTasks.map(t => t.id === taskId ? persisted : t);
      saveToStorage();
      return persisted;
    }

    const index = sessionTasks.findIndex(t => t.id === taskId);
    if (index === -1) throw new Error(`Tache ${taskId} non trouvee`);
    sessionTasks[index] = {
      ...sessionTasks[index],
      ...updates,
      updatedAt: new Date().toISOString(),
      completedAt: updates.status === 'done' ? new Date().toISOString() : (updates.status === 'todo' ? undefined : sessionTasks[index].completedAt)
    };
    saveToStorage();
    return sessionTasks[index];
  },

  async deleteTask(taskId: string): Promise<void> {
    if (isDbMode) {
      const result = await safeFetch(`${API_BASE}?action=delete_task`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: taskId })
      });
      if (!result || result.error) {
        throw new Error(result?.error || 'Suppression de la tache echouee');
      }
    }
    sessionTasks = sessionTasks.filter(t => t.id !== taskId);
    saveToStorage();
  },

  async checkUpcomingTaskReminders(userId: string): Promise<void> {
    const now = Date.now();
    const horizonMs = 1000 * 60 * 60 * 2; // 2h
    const upcoming = sessionTasks.filter(t => t.status === 'todo' && t.createdBy === userId);

    for (const task of upcoming) {
      const dueMs = new Date(task.dueAt).getTime();
      if (Number.isNaN(dueMs)) continue;
      const remaining = dueMs - now;
      if (remaining < 0 || remaining > horizonMs) continue;

      const existingNotif = sessionNotifications.find(n =>
        n.userId === userId &&
        n.type === NotificationType.TASK_DUE_SOON &&
        n.metadata?.taskId === task.id
      );
      if (existingNotif) continue;

      await api.createNotification({
        userId,
        type: NotificationType.TASK_DUE_SOON,
        title: 'Tache a echeance proche',
        message: `${task.title} arrive a echeance bientot`,
        leadId: task.leadId,
        leadName: task.clientCompanyName || undefined,
        isRead: false,
        metadata: { taskId: task.id, dueAt: task.dueAt }
      });
    }
  },

  // ===== ATTACHMENTS =====

  async uploadAttachment(leadId: string, file: File): Promise<any> {
    const formData = new FormData();
    formData.append('lead_id', leadId);
    formData.append('file', file);

    const token = sessionStorage.getItem(SESSION_KEY_ACCESS_TOKEN) || localStorage.getItem('auth_token');
    const res = await fetch(`${API_BASE}?action=upload_attachment`, {
      method: 'POST',
      credentials: 'include',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData
    });
    const text = await res.text();
    let data: any;
    try {
      data = JSON.parse(text);
    } catch {
      console.error('Upload response not JSON:', text);
      throw new Error('Erreur serveur lors de l\'upload');
    }
    if (data.error) throw new Error(data.error);
    return data;
  },

  async getAttachments(leadId: string): Promise<any[]> {
    if (!isDbMode) return [];
    const data = await safeFetch(`${API_BASE}?action=get_attachments&lead_id=${encodeURIComponent(leadId)}`);
    return Array.isArray(data) ? data : [];
  },

  async downloadAttachment(attId: string): Promise<void> {
    const response = await safeFetchRaw(`${API_BASE}?action=download_attachment&id=${encodeURIComponent(attId)}`);
    if (!response.ok) throw new Error('Echec du telechargement');
    const disposition = response.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="?([^"]+)"?/);
    const filename = match ? match[1] : 'fichier';
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },

  async deleteAttachment(attId: string): Promise<void> {
    const result = await safeFetch(`${API_BASE}?action=delete_attachment`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: attId })
    });
    if (result?.error) throw new Error(result.error);
  },

  // ===== PROFIL UTILISATEUR =====

  async updateProfile(data: { email?: string; password?: string; current_password: string }): Promise<User> {
    if (isDbMode) {
      const result = await safeFetch(`${API_BASE}?action=update_profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!result || result.error) throw new Error(result?.error || 'Erreur mise à jour profil');
      return { id: result.id, companyId: result.company_id, name: result.name, email: result.email, role: result.role } as User;
    }
    throw new Error('Non disponible en mode démo');
  },

  // ===== AUDIT LOG =====

  async getAuditLogs(params: { page?: number; limit?: number; filterAction?: string; filterUser?: string } = {}): Promise<{ logs: Array<{ id: number; user_id: string; user_name: string; user_role: string; action: string; target_type: string; target_id: string; details: string; ip_address: string; created_at: string }>; total: number; page: number; pages: number }> {
    if (isDbMode) {
      const q = new URLSearchParams();
      q.set('action', 'get_audit_logs');
      if (params.page) q.set('page', String(params.page));
      if (params.limit) q.set('limit', String(params.limit));
      if (params.filterAction) q.set('filter_action', params.filterAction);
      if (params.filterUser) q.set('filter_user', params.filterUser);
      const data = await safeFetch(`${API_BASE}?${q.toString()}`);
      return data || { logs: [], total: 0, page: 1, pages: 0 };
    }
    return { logs: [], total: 0, page: 1, pages: 0 };
  },

  // ===== EMAIL TEMPLATES =====

  async getEmailTemplates(): Promise<Array<{ id: string; name: string; subject: string; body: string; created_at: string }>> {
    if (isDbMode) {
      const data = await safeFetch(`${API_BASE}?action=get_email_templates`);
      return Array.isArray(data) ? data : [];
    }
    return [];
  },

  async saveEmailTemplate(tpl: { id?: string; name: string; subject: string; body: string }): Promise<{ id: string }> {
    if (isDbMode) {
      const result = await safeFetch(`${API_BASE}?action=save_email_template`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(tpl)
      });
      if (!result || result.error) throw new Error(result?.error || 'Erreur sauvegarde template');
      return result;
    }
    return { id: 'local_' + Date.now() };
  },

  async getSmtpSettings(): Promise<{ smtp_host: string; smtp_port: number; smtp_enc: string; smtp_user: string; from_name: string; from_email: string; has_password: boolean } | null> {
    if (!isDbMode) return null;
    try {
      return await safeFetch(`${API_BASE}?action=get_smtp_settings`);
    } catch { return null; }
  },

  async saveSmtpSettings(settings: { smtp_host: string; smtp_port: number; smtp_enc: string; smtp_user: string; smtp_pass?: string; from_name: string; from_email: string }): Promise<void> {
    const result = await safeFetch(`${API_BASE}?action=save_smtp_settings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(settings),
      timeout: 15000
    });
    if (!result?.success) throw new Error(result?.error || 'Erreur sauvegarde SMTP');
  },

  async getGlobalSmtp(): Promise<{ smtp_host: string; smtp_port: number; smtp_enc: string; smtp_user: string; from_name: string; from_email: string; has_password: boolean } | null> {
    try {
      return await safeFetch(`${API_BASE}?action=get_global_smtp`);
    } catch { return null; }
  },

  async saveGlobalSmtp(settings: { smtp_host: string; smtp_port: number; smtp_enc: string; smtp_user: string; smtp_pass?: string; from_name: string; from_email: string }): Promise<void> {
    const result = await safeFetch(`${API_BASE}?action=save_global_smtp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(settings),
      timeout: 15000
    });
    if (!result?.success) throw new Error(result?.error || 'Erreur sauvegarde SMTP global');
  },

  async testSmtp(): Promise<{ success: boolean; message?: string; error?: string }> {
    const result = await safeFetch(`${API_BASE}?action=test_smtp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
      timeout: 30000
    });
    return result || { success: false, error: 'Erreur reseau' };
  },

  async deleteEmailTemplate(id: string): Promise<void> {
    if (isDbMode) {
      const result = await safeFetch(`${API_BASE}?action=delete_email_template`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id })
      });
      if (!result || result.error) throw new Error(result?.error || 'Erreur suppression template');
    }
  },

  // ===== CALL LOGGING =====

  async logCall(payload: { leadId?: string; duration: number; direction: 'inbound' | 'outbound'; notes?: string }): Promise<string> {
    if (isDbMode) {
      const result = await safeFetch(`${API_BASE}?action=log_call`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lead_id: payload.leadId, duration: payload.duration, direction: payload.direction, notes: payload.notes || '' })
      });
      if (result?.success) return result.id;
      throw new Error(result?.error || 'Echec enregistrement appel');
    }
    // Fallback local
    const callId = `call_${Date.now()}`;
    return callId;
  },

  async getCallLogs(filters: { leadId?: string; userId?: string; limit?: number }): Promise<any[]> {
    if (isDbMode) {
      const params: string[] = [];
      if (filters.leadId) params.push(`lead_id=${encodeURIComponent(filters.leadId)}`);
      if (filters.userId) params.push(`user_id=${encodeURIComponent(filters.userId)}`);
      if (filters.limit) params.push(`limit=${filters.limit}`);
      const data = await safeFetch(`${API_BASE}?action=get_call_logs&${params.join('&')}`);
      return Array.isArray(data) ? data : [];
    }
    return [];
  },

  async getCallStats(companyId: string): Promise<CallStat[]> {
    if (isDbMode) {
      const data = await safeFetch(`${API_BASE}?action=get_call_stats&company_id=${encodeURIComponent(companyId)}`);
      if (Array.isArray(data)) {
        return data.map((d: any) => ({
          userId: d.user_id,
          callCount: Number(d.call_count),
          avgDuration: Math.round(Number(d.avg_duration)),
          totalDuration: Number(d.total_duration),
          period: 'month' as const
        }));
      }
    }
    return MOCK_CALL_STATS;
  },

  // ===== OPPORTUNITES =====

  async getOpportunities(filters: { companyId: string, leadId?: string, clientCompanyName?: string }): Promise<Opportunity[]> {
    if (isDbMode) {
      const query: string[] = [`company_id=${encodeURIComponent(filters.companyId)}`];
      if (filters.leadId) query.push(`lead_id=${encodeURIComponent(filters.leadId)}`);
      if (filters.clientCompanyName) query.push(`client_company_name=${encodeURIComponent(filters.clientCompanyName)}`);
      const apiData = await safeFetch(`${API_BASE}?action=get_opportunities&${query.join('&')}`);
      if (Array.isArray(apiData)) {
        const mapped = apiData.map(mapOpportunityFromApi);
        sessionOpportunities = [
          ...sessionOpportunities.filter(o => String(o.companyId) !== String(filters.companyId)),
          ...mapped
        ];
        saveToStorage();
        return mapped;
      }
      throw new Error('Impossible de recuperer les opportunites depuis la base');
    }

    return sessionOpportunities.filter(o => {
      if (String(o.companyId) !== String(filters.companyId)) return false;
      if (filters.leadId && String(o.leadId || '') !== String(filters.leadId)) return false;
      if (filters.clientCompanyName && String(o.clientCompanyName || '') !== String(filters.clientCompanyName)) return false;
      return true;
    });
  },

  async createOpportunity(
    payload: {
      companyId: string;
      leadId?: string;
      clientCompanyName: string;
      subject: string;
      comment?: string;
      createdBy: string;
      items: Array<{ label: string; quantity: number; unitPrice: number }>;
    },
    currentUser?: { id: string; name: string }
  ): Promise<Opportunity> {
    const now = new Date().toISOString();
    const id = 'opp_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5);
    const items: OpportunityItem[] = payload.items.map((item, idx) => {
      const totalPrice = Number((item.quantity * item.unitPrice).toFixed(2));
      return {
        id: `oppi_${Date.now()}_${idx}_${Math.random().toString(36).substr(2, 4)}`,
        opportunityId: id,
        label: item.label,
        quantity: Number(item.quantity),
        unitPrice: Number(item.unitPrice),
        totalPrice
      };
    });
    const totalAmount = Number(items.reduce((sum, item) => sum + item.totalPrice, 0).toFixed(2));

    const newOpportunity: Opportunity = {
      id,
      companyId: payload.companyId,
      leadId: payload.leadId,
      clientCompanyName: payload.clientCompanyName,
      subject: payload.subject,
      comment: payload.comment || '',
      totalAmount,
      createdBy: payload.createdBy,
      createdAt: now,
      items
    };

    if (isDbMode) {
      const apiResult = await safeFetch(`${API_BASE}?action=create_opportunity`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: newOpportunity.id,
          companyId: newOpportunity.companyId,
          leadId: newOpportunity.leadId,
          clientCompanyName: newOpportunity.clientCompanyName,
          subject: newOpportunity.subject,
          comment: newOpportunity.comment,
          totalAmount: newOpportunity.totalAmount,
          createdBy: newOpportunity.createdBy,
          items: newOpportunity.items.map(i => ({
            id: i.id,
            label: i.label,
            quantity: i.quantity,
            unitPrice: i.unitPrice
          }))
        })
      });
      if (!apiResult || !apiResult.id) {
        throw new Error('Creation de l opportunite echouee cote base');
      }
      const persisted = mapOpportunityFromApi(apiResult);
      sessionOpportunities = [persisted, ...sessionOpportunities];
      saveToStorage();

      if (currentUser && persisted.leadId) {
        createActivity(
          persisted.leadId,
          currentUser.id,
          currentUser.name,
          LeadActivityType.INFO_UPDATED,
          `Nouvelle opportunite creee : ${persisted.subject}`,
          { opportunityId: persisted.id, totalAmount: persisted.totalAmount }
        );
      }
      return persisted;
    }

    sessionOpportunities = [newOpportunity, ...sessionOpportunities];
    saveToStorage();

    if (currentUser && newOpportunity.leadId) {
      createActivity(
        newOpportunity.leadId,
        currentUser.id,
        currentUser.name,
        LeadActivityType.INFO_UPDATED,
        `Nouvelle opportunite creee : ${newOpportunity.subject}`,
        { opportunityId: newOpportunity.id, totalAmount: newOpportunity.totalAmount }
      );
    }
    return newOpportunity;
  },

  async updateOpportunity(opportunityId: string, updates: { subject: string, comment?: string, clientCompanyName?: string, items: OpportunityItem[] }): Promise<void> {
    if (isDbMode) {
      const result = await safeFetch(`${API_BASE}?action=update_opportunity`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: opportunityId, ...updates })
      });
      if (!result || result.error) {
        throw new Error(result?.error || 'Mise a jour de l\'opportunite echouee');
      }
    }
    const totalAmount = updates.items.reduce((sum, it) => sum + (it.quantity * it.unitPrice), 0);
    const index = sessionOpportunities.findIndex(o => o.id === opportunityId);
    if (index !== -1) {
      sessionOpportunities[index] = {
        ...sessionOpportunities[index],
        subject: updates.subject,
        comment: updates.comment || '',
        clientCompanyName: updates.clientCompanyName || sessionOpportunities[index].clientCompanyName,
        items: updates.items,
        totalAmount
      };
      saveToStorage();
    }
  },

  async deleteOpportunity(opportunityId: string): Promise<void> {
    if (isDbMode) {
      const result = await safeFetch(`${API_BASE}?action=delete_opportunity`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: opportunityId })
      });
      if (!result || result.error) {
        throw new Error(result?.error || 'Suppression de l\'opportunite echouee');
      }
    }
    sessionOpportunities = sessionOpportunities.filter(o => o.id !== opportunityId);
    saveToStorage();
  },

  // ===== RENDEZ-VOUS =====

  async getAppointments(filters: { leadId?: string, userId?: string }): Promise<Appointment[]> {
    if (isDbMode) {
      const q: string[] = [];
      if (filters?.leadId) q.push(`lead_id=${encodeURIComponent(filters.leadId)}`);
      if (filters?.userId) q.push(`user_id=${encodeURIComponent(filters.userId)}`);
      const url = `${API_BASE}?action=get_appointments${q.length ? `&${q.join('&')}` : ''}`;
      const apiData = await safeFetch(url);
      if (Array.isArray(apiData)) {
        const mapped = apiData.map(mapAppointmentFromApi);
        if (filters?.leadId) {
          sessionAppointments = [
            ...sessionAppointments.filter(a => String(a.leadId) !== String(filters.leadId)),
            ...mapped
          ];
        } else {
          sessionAppointments = mapped;
        }
        return mapped;
      }
      throw new Error('Impossible de recuperer les rendez-vous depuis la base');
    }

    let filtered = [...sessionAppointments];
    if (filters) {
      if (filters.leadId) filtered = filtered.filter(a => String(a.leadId) === String(filters.leadId));
      if (filters.userId) filtered = filtered.filter(a => String(a.userId) === String(filters.userId));
    }
    return filtered;
  },

  async addAppointment(appt: Omit<Appointment, 'id'>, currentUser?: { id: string, name: string }): Promise<Appointment> {
    const newAppt = { 
      ...appt, 
      id: 'appt_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5) 
    };
    if (isDbMode) {
      const apiResult = await safeFetch(`${API_BASE}?action=add_appointment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newAppt)
      });
      if (!apiResult || !apiResult.id) {
        throw new Error('Creation du rendez-vous echouee cote base');
      }
      const persisted = mapAppointmentFromApi(apiResult);
      sessionAppointments.push(persisted);

      if (currentUser && appt.leadId) {
        const appointmentDate = new Date(appt.start).toLocaleDateString('fr-FR', {
          day: '2-digit',
          month: 'long',
          hour: '2-digit',
          minute: '2-digit'
        });
        createActivity(
          appt.leadId,
          currentUser.id,
          currentUser.name,
          LeadActivityType.APPOINTMENT_CREATED,
          `Rendez-vous planifie : ${appt.title} le ${appointmentDate}`,
          { appointmentId: persisted.id, appointmentType: appt.type }
        );
      }

      saveToStorage();
      return persisted;
    }

    sessionAppointments.push(newAppt);
    
    // ACTIVITÉ
    if (currentUser && appt.leadId) {
      const appointmentDate = new Date(appt.start).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'long',
        hour: '2-digit',
        minute: '2-digit'
      });
      createActivity(
        appt.leadId,
        currentUser.id,
        currentUser.name,
        LeadActivityType.APPOINTMENT_CREATED,
        `Rendez-vous planifié : ${appt.title} le ${appointmentDate}`,
        { appointmentId: newAppt.id, appointmentType: appt.type }
      );

      // NOTIFICATION : Rendez-vous créé
      const lead = sessionLeads.find(l => l.id === appt.leadId);
      if (lead && lead.assignedTo && lead.assignedTo !== currentUser.id) {
        await api.createNotification({
          userId: lead.assignedTo,
          type: NotificationType.APPOINTMENT_CREATED,
          title: 'Nouveau rendez-vous',
          message: `${currentUser.name} a planifié un rendez-vous : ${appt.title}`,
          leadId: appt.leadId,
          leadName: lead.name,
          isRead: false,
          metadata: { appointmentId: newAppt.id }
        });
      }
    }
    
    saveToStorage();
    return newAppt;
  },
  
  async updateAppointment(id: string, updates: Partial<Appointment>): Promise<Appointment | null> {
    if (isDbMode) {
      const apiResult = await safeFetch(`${API_BASE}?action=update_appointment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, ...updates })
      });
      if (!apiResult || apiResult.error) return null;
    }
    const idx = sessionAppointments.findIndex(a => a.id === id);
    if (idx === -1) return null;
    sessionAppointments[idx] = { ...sessionAppointments[idx], ...updates };
    saveToStorage();
    return sessionAppointments[idx];
  },
  
  async deleteAppointment(id: string): Promise<void> {
    if (isDbMode) {
      const apiResult = await safeFetch(`${API_BASE}?action=delete_appointment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id })
      });
      if (!apiResult || apiResult.error) {
        throw new Error('Suppression du rendez-vous echouee cote base');
      }
    }
    sessionAppointments = sessionAppointments.filter(a => a.id !== id);
    saveToStorage();
  },

  // ===== COMMENTAIRES =====

  async getComments(leadId: string): Promise<LeadComment[]> {
    if (isDbMode) {
      const apiData = await safeFetch(`${API_BASE}?action=get_comments&lead_id=${encodeURIComponent(leadId)}`);
      if (Array.isArray(apiData)) {
        localComments = localComments
          .filter(c => String(c.leadId) !== String(leadId))
          .concat(apiData.map(mapCommentFromApi));
        return apiData.map(mapCommentFromApi);
      }
      throw new Error('Impossible de recuperer les commentaires depuis la base');
    }
    return localComments.filter(c => String(c.leadId) === String(leadId));
  },

  async addComment(comment: Omit<LeadComment, 'id' | 'createdAt'>, currentUser?: { id: string, name: string }): Promise<LeadComment> {
    const newComment: LeadComment = { 
      ...comment, 
      id: 'lc_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5), 
      createdAt: new Date().toISOString() 
    };
    if (isDbMode) {
      const apiResult = await safeFetch(`${API_BASE}?action=add_comment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(comment)
      });
      if (!apiResult || !apiResult.id) {
        throw new Error('Creation du commentaire echouee cote base');
      }
      const persisted = mapCommentFromApi(apiResult);
      localComments.push(persisted);

      if (currentUser) {
        createActivity(
          comment.leadId,
          currentUser.id,
          currentUser.name,
          LeadActivityType.COMMENT_ADDED,
          `Commentaire ajoute : "${comment.content.substring(0, 50)}${comment.content.length > 50 ? '...' : ''}"`
        );
      }
      return persisted;
    }

    localComments.push(newComment);
    
    // ACTIVITÉ
    if (currentUser) {
      createActivity(
        comment.leadId,
        currentUser.id,
        currentUser.name,
        LeadActivityType.COMMENT_ADDED,
        `Commentaire ajouté : "${comment.content.substring(0, 50)}${comment.content.length > 50 ? '...' : ''}"`
      );

      // NOTIFICATIONS
      // 1. Détecter les mentions (@utilisateur)
      const mentions = comment.content.match(/@(\w+)/g);
      if (mentions) {
        const mentionedUsers = sessionUsers.filter(u => 
          mentions.some(m => u.name.toLowerCase().includes(m.slice(1).toLowerCase()))
        );
        
        for (const user of mentionedUsers) {
          if (user.id !== currentUser.id) {
            await api.createNotification({
              userId: user.id,
              type: NotificationType.MENTION,
              title: 'Vous avez été mentionné',
              message: `${currentUser.name} vous a mentionné dans un commentaire`,
              leadId: comment.leadId,
              isRead: false
            });
          }
        }
      }

      // 2. Notifier l'assigné du prospect
      const lead = sessionLeads.find(l => l.id === comment.leadId);
      if (lead && lead.assignedTo && lead.assignedTo !== currentUser.id) {
        await api.createNotification({
          userId: lead.assignedTo,
          type: NotificationType.NEW_COMMENT,
          title: 'Nouveau commentaire',
          message: `${currentUser.name} a ajouté un commentaire sur ${lead.name}`,
          leadId: comment.leadId,
          leadName: lead.name,
          isRead: false
        });
      }
    }
    
    return newComment;
  },

  // ===== TIMELINE (ACTIVITÉS) =====

  async getLeadActivities(leadId: string): Promise<LeadActivity[]> {
    if (isDbMode) {
      const apiData = await safeFetch(`${API_BASE}?action=get_lead_activities&lead_id=${encodeURIComponent(leadId)}`);
      if (Array.isArray(apiData)) {
        const mapped = apiData.map(mapActivityFromApi);
        sessionActivities = [
          ...sessionActivities.filter(a => String(a.leadId) !== String(leadId)),
          ...mapped
        ];
        return mapped.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      }
      throw new Error('Impossible de recuperer la timeline depuis la base');
    }
    const activities = sessionActivities.filter(a => String(a.leadId) === String(leadId));
    return activities.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  },

  async addLeadActivity(activity: Omit<LeadActivity, 'id' | 'createdAt'>): Promise<LeadActivity> {
    if (isDbMode) {
      const payload = {
        ...activity,
        id: 'activity_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5)
      };
      const apiResult = await safeFetch(`${API_BASE}?action=add_lead_activity`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!apiResult || !apiResult.id) {
        throw new Error('Creation de timeline echouee cote base');
      }
      const persisted = mapActivityFromApi(apiResult);
      sessionActivities.push(persisted);
      saveToStorage();
      return persisted;
    }
    return createActivity(
      activity.leadId,
      activity.userId,
      activity.userName,
      activity.type,
      activity.description,
      activity.metadata
    );
  },

  // ===== NOTIFICATIONS =====

  async getNotifications(userId: string): Promise<Notification[]> {
    if (isDbMode) {
      try {
        const apiData = await safeFetch(`${API_BASE}?action=get_notifications&user_id=${encodeURIComponent(userId)}`);
        if (Array.isArray(apiData)) {
          const mapped: Notification[] = apiData.map((n: any) => ({
            id: n.id,
            userId: n.user_id,
            type: n.type as NotificationType,
            title: n.title,
            message: n.message,
            leadId: n.lead_id || undefined,
            leadName: n.lead_name || undefined,
            isRead: !!n.is_read,
            createdAt: n.created_at,
            metadata: n.metadata || undefined,
          }));
          sessionNotifications = [
            ...mapped,
            ...sessionNotifications.filter(sn => sn.userId !== userId)
          ];
          return mapped;
        }
      } catch (e) {
        console.warn('get_notifications API failed, fallback local', e);
      }
    }
    const userNotifications = sessionNotifications
      .filter(n => n.userId === userId)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    return userNotifications;
  },

  async createNotification(notification: Omit<Notification, 'id' | 'createdAt'>): Promise<Notification> {
    const newNotification: Notification = {
      ...notification,
      id: 'notif_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5),
      createdAt: new Date().toISOString()
    };

    sessionNotifications.push(newNotification);
    saveToStorage();

    if (isDbMode) {
      safeFetch(`${API_BASE}?action=create_notification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: newNotification.id,
          userId: notification.userId,
          type: notification.type,
          title: notification.title,
          message: notification.message,
          leadId: notification.leadId || null,
          leadName: notification.leadName || null,
          metadata: notification.metadata || null,
        })
      }).catch(err => console.warn('create_notification sync failed:', err.message));
    }

    return newNotification;
  },

  async markNotificationAsRead(notificationId: string): Promise<void> {
    const index = sessionNotifications.findIndex(n => n.id === notificationId);
    if (index !== -1) {
      sessionNotifications[index].isRead = true;
      saveToStorage();
    }
    if (isDbMode) {
      safeFetch(`${API_BASE}?action=mark_notification_read`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: notificationId })
      }).catch(err => console.warn('mark_notification_read sync failed:', err.message));
    }
  },

  async markAllNotificationsAsRead(userId: string): Promise<void> {
    sessionNotifications = sessionNotifications.map(n =>
      n.userId === userId ? { ...n, isRead: true } : n
    );
    saveToStorage();
    if (isDbMode) {
      safeFetch(`${API_BASE}?action=mark_all_notifications_read`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId })
      }).catch(err => console.warn('mark_all_notifications_read sync failed:', err.message));
    }
  },

  async deleteNotification(notificationId: string): Promise<void> {
    sessionNotifications = sessionNotifications.filter(n => n.id !== notificationId);
    saveToStorage();
    if (isDbMode) {
      safeFetch(`${API_BASE}?action=delete_notification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: notificationId })
      }).catch(err => console.warn('delete_notification sync failed:', err.message));
    }
  },

  async getUnreadNotificationsCount(userId: string): Promise<number> {
    return sessionNotifications.filter(n => n.userId === userId && !n.isRead).length;
  },

  // Vérifier les prospects inactifs (à appeler périodiquement)
  async checkInactiveLeads(): Promise<void> {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    
    const inactiveLeads = sessionLeads.filter(lead => {
      const lastActivity = sessionActivities
        .filter(a => a.leadId === lead.id)
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
      
      if (!lastActivity) return false;
      
      const activityDate = new Date(lastActivity.createdAt);
      return activityDate < sevenDaysAgo && lead.status !== 'won' && lead.status !== 'lost';
    });
    
    for (const lead of inactiveLeads) {
      // Vérifier si on n'a pas déjà envoyé une notification récente
      const existingNotif = sessionNotifications.find(n => 
        n.leadId === lead.id && 
        n.type === NotificationType.LEAD_INACTIVE &&
        new Date(n.createdAt) > sevenDaysAgo
      );
      
      if (!existingNotif && lead.assignedTo) {
        await api.createNotification({
          userId: lead.assignedTo,
          type: NotificationType.LEAD_INACTIVE,
          title: 'Prospect inactif',
          message: `Aucune activité sur ${lead.name} depuis plus de 7 jours`,
          leadId: lead.id,
          leadName: lead.name,
          isRead: false
        });
      }
    }
  }
};

// Export CSV amélioré
export const exportToCSV = (data: any[], filename: string) => {
  if (!data || !data.length) {
    alert('⚠️ Aucune donnée à exporter');
    return;
  }
  
  try {
    const separator = ',';
    const keys = Object.keys(data[0]);
    const csvContent =
      keys.join(separator) +
      '\n' +
      data.map(row => {
        return keys.map(k => {
          let cell = row[k] === null || row[k] === undefined ? '' : row[k];
          cell = cell instanceof Date ? cell.toLocaleString() : cell.toString().replace(/"/g, '""');
          if (cell.search(/("|,|\n)/g) >= 0) cell = `"${cell}"`;
          return cell;
        }).join(separator);
      }).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      console.log(`✓ Export CSV réussi: ${filename}`);
    }
  } catch (error) {
    console.error('Erreur export CSV:', error);
    alert('⚠️ Erreur lors de l\'export CSV');
  }
};

